import { useEffect, useMemo, useState } from "react";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import ReminderService from "../Services/ReminderService";
import { registerLocale } from "react-datepicker";
import { format, parseJSON } from "date-fns";
import ReminderModel from "../Models/ReminderModel";
import nlBE from "date-fns/locale/nl-BE";
import "react-datepicker/dist/react-datepicker.css";
import GraphService from "../Services/GraphService";
import PeopleService from "../Services/PeopleService";
import { useMsal } from "@azure/msal-react";
import { User } from "@microsoft/microsoft-graph-types";
import { toast } from "react-toastify";
import ReminderModal from "./ReminderModal";

interface RemindersModalProps {
  code: string;
  afterClose: Function;
}

const RemindersModal = (props: RemindersModalProps) => {
  const { instance } = useMsal();

  const [visible, setVisible] = useState<boolean>(true);
  const [showDetail, setShowDetail] = useState<boolean>(false);
  const [reminders, setReminders] = useState<ReminderModel[]>([]);
  const [currentUser, setCurrentUser] = useState<User>();

  const [reminder, setReminder] = useState<ReminderModel>({});

  const reminderService = useMemo(
    () => new ReminderService(instance),
    [instance]
  );
  const graphService = useMemo(() => new GraphService(instance), [instance]);
  const peopleService = useMemo(() => new PeopleService(instance), [instance]);

  registerLocale("nlBE", nlBE);

  useEffect(() => {
    const fetchData = async () => {
      const reminders = await reminderService.get(props.code);
      setReminders(reminders);

      const user = await graphService.getUserDetails();
      setCurrentUser(user);
    };

    fetchData();
  }, [props, props.code, reminderService, graphService, peopleService]);

  const modalToggle = () => {
    setVisible((visible) => {
      return !visible;
    });

    props.afterClose();
  };

  const reminderModalAfterClose = async () => {
    try {
      setShowDetail(false);
      const reminders = await reminderService.get(props.code);
      setReminders(reminders);
    } catch (error) {
      toast.error("Fout bij het ophalen van de data.");
    }
  };

  const renderDetailModal = () => {
    return (
      <ReminderModal
        code={props.code}
        reminder={reminder}
        afterClose={() => reminderModalAfterClose()}
      />
    );
  };

  return (
    <>
      <Modal
        isOpen={visible}
        toggle={() => modalToggle()}
        className="mw-100 w-75"
      >
        <ModalHeader toggle={() => modalToggle()}>
          {props.code} - Reminders
        </ModalHeader>
        <ModalBody>
          <table className="table table-sm text-nowrap">
            <thead>
              <tr>
                <th>Datum</th>
                <th>Reminder</th>
                <th>Persoon</th>
                <th>Verwerkt</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {reminders?.map((record, index) => {
                return (
                  <tr key={index}>
                    <td>
                      {record.date === undefined
                        ? null
                        : format(parseJSON(record.date), "dd/MM/yyyy")}
                    </td>
                    <td>{record.description}</td>
                    <td>{record.personName}</td>
                    <td>{record.completed}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-link p-0"
                        onClick={() => {
                          setReminder(record);
                          setShowDetail(true);
                        }}
                      >
                        <i className="fas fa-pen"></i>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            onClick={() => {
              setReminder({
                projectCode: props.code,
                completed: false,
                personId: currentUser?.id,
                date: format(new Date(), "yyyy-MM-dd") + "T00:00:00.000Z",
              });
              setShowDetail(true);
            }}
          >
            Nieuwe reminder
          </Button>{" "}
          <Button color="secondary" onClick={() => modalToggle()}>
            Sluiten
          </Button>
        </ModalFooter>
      </Modal>
      {showDetail && renderDetailModal()}
    </>
  );
};

export default RemindersModal;
